<template>
    <!-- 메모용 가벼운 HTML 에디터 (Quill). v-model = HTML 문자열, 예전 일반 글자 값도 받아서 변환 -->
    <!-- theme='bubble' + bare: 테두리·도구 줄 없이 글자를 선택하면 서식 말풍선 (포스트잇 메모용) -->
    <div class="rte" :class="[{ 'rte-compact': compact, 'rte-focus': focused, 'rte-bare': bare }, 'rte-' + tone, 'rte-theme-' + theme]" @click.stop>
        <QuillEditor ref="q" :key="editorKey" v-model:content="html" content-type="html" :theme="theme"
                     :toolbar="theme == 'bubble' || mini ? bubbleToolbar : toolbar" :placeholder="placeholder"
                     @focus="onFocus" @blur="onBlur" @ready="onReady"/>
    </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import '@vueup/vue-quill/dist/vue-quill.bubble.css';
import { toEditorHtml, normalizeEditorHtml } from '@/utils/richText';

export default {
    name: 'RichTextEditor',
    components: { QuillEditor },
    emits: ['update:modelValue', 'submit', 'focus', 'blur'],
    props: {
        modelValue: { type: String, default: '' },
        placeholder: { type: String, default: '메모를 입력하세요' },
        compact: { type: Boolean, default: false },     //도구 줄 작게
        tone: { type: String, default: 'plain' },        //plain / yellow (메모지 배경)
        minHeight: { type: Number, default: 80 },
        maxHeight: { type: Number, default: 260 },
        theme: { type: String, default: 'snow' },        //snow = 위 도구 줄 / bubble = 글자 선택 시 말풍선
        bare: { type: Boolean, default: false },         //테두리·배경 없음
        mini: { type: Boolean, default: false },         //도구 줄을 짧게 (굵게·기울임·밑줄·취소선·색·목록·체크·링크)
    },
    data() {
        return {
            html: toEditorHtml(this.modelValue),
            lastEmitted: null,
            editorKey: 0,
            focused: false,
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
                ['link', 'clean'],
            ],
            bubbleToolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }],
                [{ list: 'bullet' }, { list: 'check' }],
                ['link'],
            ],
        };
    },
    watch: {
        html(v) {
            const out = normalizeEditorHtml(v);
            this.lastEmitted = out;
            if (out !== this.modelValue) this.$emit('update:modelValue', out);
        },
        //밖에서 값이 바뀌면(다른 동물 열기·저장 후 초기화 등) 에디터 다시
        modelValue(v) {
            if (v === this.lastEmitted) return;
            this.html = toEditorHtml(v);
            this.lastEmitted = v;
            this.editorKey++;
        },
    },
    methods: {
        onFocus() { this.focused = true; this.$emit('focus'); },
        onBlur() { this.focused = false; this.$emit('blur'); },
        onReady(quill) {
            const root = quill.root;
            root.style.minHeight = this.minHeight + 'px';
            root.style.maxHeight = this.maxHeight + 'px';
            //Ctrl/⌘ + Enter = 저장
            root.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); this.$emit('submit'); }
            });
        },
        focus() {
            const q = this.$refs.q && this.$refs.q.getQuill && this.$refs.q.getQuill();
            if (q) q.focus();
        },
        clear() {
            this.html = '';
            this.lastEmitted = '';
            this.editorKey++;
        },
    },
};
</script>

<style scoped>
    /* 메모 에디터: 테두리 없이 (쓰는 중엔 옅은 주황 링만) */
    /* 배경은 메모 박스 색을 그대로 (투명) */
    .rte { border: 1px solid transparent; border-radius: 10px; background: transparent; overflow: hidden; text-align: left; transition: box-shadow .15s; }
    .rte.rte-focus { box-shadow: 0 0 0 2px rgba(246, 149, 28, 0.28); }
    .rte.rte-yellow { background: transparent; }
    .rte :deep(.ql-toolbar.ql-snow) { border: none; border-bottom: 1px solid #eee; padding: 4px 6px; }
    .rte.rte-yellow :deep(.ql-toolbar.ql-snow) { border-bottom-color: #F3E3A8; }
    .rte :deep(.ql-container.ql-snow) { border: none; font-family: inherit; font-size: 14px; }
    .rte :deep(.ql-editor) { padding: 8px 12px; line-height: 1.6; overflow-y: auto; color: #333; }
    .rte :deep(.ql-editor p), .rte :deep(.ql-editor li) { font-size: 14px !important; color: #333 !important; margin: 0 !important; white-space: pre-wrap; }
    .rte :deep(.ql-editor.ql-blank::before) { font-style: normal; color: #aaa; left: 12px; }
    .rte.rte-compact :deep(.ql-toolbar.ql-snow) { padding: 2px 4px; }
    .rte.rte-compact :deep(.ql-toolbar.ql-snow .ql-formats) { margin-right: 6px; }
    .rte.rte-compact :deep(.ql-toolbar button) { width: 24px; height: 22px; padding: 2px 3px; }
    /* 포스트잇용: 테두리·배경 없이 */
    .rte.rte-bare { border: none !important; background: transparent !important; box-shadow: none !important; overflow: visible; }
    .rte.rte-bare :deep(.ql-container.ql-bubble) { font-family: inherit; font-size: 14px; }
    .rte.rte-bare :deep(.ql-editor) { padding: 0; }
    .rte.rte-bare :deep(.ql-container.ql-snow) { font-family: inherit; font-size: 14px; }
    .rte.rte-bare :deep(.ql-toolbar.ql-snow) { padding: 0 0 4px; margin: 0 0 6px; border: none; border-bottom: 1px dashed rgba(0, 0, 0, 0.15); }
    .rte.rte-bare :deep(.ql-toolbar.ql-snow .ql-formats) { margin-right: 2px !important; }
    .rte.rte-bare :deep(.ql-toolbar.ql-snow button), .rte.rte-bare :deep(.ql-toolbar.ql-snow .ql-picker) { width: 24px !important; height: 24px !important; padding: 3px !important; border-radius: 5px; }
    .rte.rte-bare :deep(.ql-toolbar.ql-snow .ql-color-picker .ql-picker-label) { padding: 0 !important; }
    .rte.rte-bare :deep(.ql-toolbar button:hover), .rte.rte-bare :deep(.ql-toolbar button.ql-active) { background: rgba(255, 255, 255, 0.7); }
    .rte.rte-bare :deep(.ql-editor.ql-blank::before) { left: 0; right: 0; }
    .rte :deep(.ql-bubble .ql-tooltip) { z-index: 5; border-radius: 8px; }
    @media (max-width: 768px) {
        .rte :deep(.ql-editor p), .rte :deep(.ql-editor li) { font-size: 16px !important; }   /* iOS 확대 방지 */
        .rte :deep(.ql-toolbar button) { width: 30px; height: 28px; }
    }
</style>
